
"use client";

import { createContext, useContext, useEffect, useState, type ReactNode, useCallback } from 'react';
import { type PayoutMethod } from '@/lib/data';
import { db } from '@/lib/firebase';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';

interface PayoutMethodContextType {
  payoutMethods: PayoutMethod[];
  addPayoutMethod: (method: Omit<PayoutMethod, 'id'>) => Promise<void>;
  updatePayoutMethod: (id: string, updatedMethod: Partial<Omit<PayoutMethod, 'id'>>) => Promise<void>;
  deletePayoutMethod: (id: string) => Promise<void>;
  loading: boolean;
  refreshPayoutMethods: () => void;
}

const PayoutMethodContext = createContext<PayoutMethodContextType | undefined>(undefined);

export const PayoutMethodProvider = ({ children }: { children: ReactNode }) => {
  const [payoutMethods, setPayoutMethods] = useState<PayoutMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchPayoutMethods = useCallback(() => {
    setLoading(true);
    const methodsCollection = collection(db, 'payoutMethods');
    const q = query(methodsCollection, orderBy('minWithdrawal', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const methodList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: doc.data().type || 'fiat',
      } as PayoutMethod));
      setPayoutMethods(methodList);
      setLoading(false);
    }, (error) => {
      console.error("Failed to fetch payout methods from Firestore", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not load payout methods from the database." });
      setLoading(false);
    });

    return unsubscribe;
  }, [toast]);

  useEffect(() => {
    const unsubscribe = fetchPayoutMethods();
    return () => unsubscribe();
  }, [fetchPayoutMethods]);

  const addPayoutMethod = async (methodData: Omit<PayoutMethod, 'id'>) => {
    const dataToSave: any = { ...methodData };
    if (methodData.fixedAmounts && methodData.fixedAmounts.length > 0) {
      dataToSave.minWithdrawal = Math.min(...methodData.fixedAmounts);
    } else if (methodData.type === 'raffle') {
      dataToSave.minWithdrawal = methodData.entryCost;
    }

    try {
      await addDoc(collection(db, 'payoutMethods'), dataToSave);
      // State will update via the onSnapshot listener
    } catch (error) {
      console.error("Error adding payout method:", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not add the payout method." });
      throw error;
    }
  };

  const updatePayoutMethod = async (id: string, updatedMethodData: Partial<Omit<PayoutMethod, 'id'>>) => {
     try {
       if (Object.keys(updatedMethodData).length === 0) {
           toast({ title: "No changes to save." });
           return;
       }
       
       const dataToSave: any = { ...updatedMethodData };
        if (updatedMethodData.fixedAmounts && updatedMethodData.fixedAmounts.length > 0) {
            dataToSave.minWithdrawal = Math.min(...updatedMethodData.fixedAmounts);
        } else if (updatedMethodData.fixedAmounts?.length === 0) {
            dataToSave.minWithdrawal = updatedMethodData.minWithdrawal || 0;
        } else if (updatedMethodData.type === 'raffle' && updatedMethodData.entryCost) {
            dataToSave.minWithdrawal = updatedMethodData.entryCost;
        }

       await updateDoc(doc(db, 'payoutMethods', id), dataToSave);
       // State will update via the onSnapshot listener
     } catch (error) {
       console.error("Error updating payout method:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not update the payout method." });
       throw error;
     }
  };

  const deletePayoutMethod = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'payoutMethods', id));
      // State will update via the onSnapshot listener
    } catch (error) {
       console.error("Error deleting payout method:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not delete the payout method." });
       throw error;
    }
  };

  return (
    <PayoutMethodContext.Provider value={{ payoutMethods, addPayoutMethod, updatePayoutMethod, deletePayoutMethod, loading, refreshPayoutMethods: fetchPayoutMethods }}>
      {children}
    </PayoutMethodContext.Provider>
  );
};

export const usePayoutMethods = () => {
  const context = useContext(PayoutMethodContext);
  if (context === undefined) {
    throw new Error('usePayoutMethods must be used within a PayoutMethodProvider');
  }
  return context;
};
